import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  serialize,
  transform,
  Transformer,
  whitelist,
} from '@jsonapiworld/jsonapi-serializer';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { addDays, endOfDay, endOfYear, formatISO, parseISO, startOfYear } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import uniqBy from 'lodash/uniqBy';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchPayrollDownloadUrl,
  fetchDocumentImportConfig,
  fetchDocumentImportTemplates,
  fetchLastProgressiveBySectional,
} from '~/api/documents';
import { backendApiGateway } from '~/client/backend';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { useHasSplitByCostCenter } from '~/hooks/useFeatureFlag';
import useFlowUpdateSideEffects from '~/hooks/useFlowUpdateSideEffects';
import { useDateRange } from '~/state/documents/summary';
import { useExcluded, useResetSelection, useSelected } from '~/state/selection';
import { useFilterState } from '~/state/tableFilters';
import { useSorting as useTableSorting } from '~/state/tableSorting';
import { Category, CategoryResourceType, Subcategory, SubcategoryResourceType } from '~/types/category';
import { Company, CompanyQueryKeys } from '~/types/company';
import { CostCenterResourceType } from '~/types/cost-center';
import { Counterpart, CounterpartResourceType } from '~/types/counterparts';
import {
  BillsFiltersFields,
  BillsWithoutVATRegistrationsFilter,
  CreateDocumentForm,
  DeliveryNotesFiltersFields,
  Document,
  DocumentBulkUpdate,
  DocumentCreator,
  DocumentDirection,
  DocumentImportQueryKeys,
  DocumentQueryKeys,
  DocumentResourceType,
  DocumentsMeta,
  DocumentsQueryFilters,
  DocumentsQuerySorting,
  DocumentsSummaryResponse,
  DocumentStatus,
  DocumentType,
  DocumentWithAttachments,
  InvoiceCreationData,
  InvoiceDashboardParams,
  InvoiceImportTemplate,
  InvoicesFiltersFields,
  QuotesFiltersFields,
  Total,
  UpdateDocumentForm,
} from '~/types/document';
import { DocumentAllocationResourceType, DocumentAllocationUpdatePayload } from '~/types/document-allocation';
import { Flow, FlowPaymentStatus, FlowsQueryKeys } from '~/types/flow';
import { InvoicesQueryKeys } from '~/types/invoicing';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { OutstandingKeys } from '~/types/outstanding';
import { ReconciliationsQueryKeys } from '~/types/reconciliation';
import { TransactionFlowDirection } from '~/types/transaction';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import { BillTypeFilter } from '~/types/vat-bookkeeping';
import { getIsInflow } from '~/utils/document';
import { sortCounterpartSummary } from '~/utils/documentsSummary';
import { downloadBlob, downloadUrl } from '~/utils/download';
import { addCategoryFilter } from '~/utils/filters';
import { formatDateForAPI, formatISOForAPI } from '~/utils/formatDate';
import {
  buildJsonAPIQueryParams,
  IJsonAPIFilterBuilder,
  jsonAPICursorPaginatorBuilder,
  jsonAPIExtraQueryBuilder,
  jsonAPIFieldsBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  SortInfo,
} from '~/utils/jsonapi-query';
import { isMoreThanZero, isZero, newMoney, subtractMoney } from '~/utils/money';
import { AnalyticsEvents } from './analytics';
import { useCreateFlow, useUpdateFlow } from './flow';
import { decorateWithReconciliations } from './reconciliation';

const DocumentsPageSize = 50;

interface DocumentUpdatePayload {
  id: Document['id'];
  documentType?: UpdateDocumentForm['documentType'];
  creationDate?: string;
  number?: UpdateDocumentForm['number'];
  counterpartName?: UpdateDocumentForm['counterpartName'];
  grossAmount?: Document['grossAmount'];
  vatAmount?: Document['vatAmount'];
  counterpart?: { id: Counterpart['id'] } | null;
  category?: { id: Category['id'] } | null;
  subcategory?: { id: Subcategory['id'] } | null;
  notes?: UpdateDocumentForm['notes'];
  allocations?: UpdateDocumentForm['allocations'];
}

const emptyDocumentsQueryFilters: DocumentsQueryFilters = {
  totalAmount: null,
  creationDate: [null, null],
  direction: null,
  documentTypes: [],
  currencies: [],
  paymentStatus: null,
  search: '',
  status: [],
};

const initialDocumentsQuerySorting: SortInfo<DocumentsQuerySorting> = {
  sort: 'searchDate',
  direction: 'desc',
};

const createSortingBuilder = (sorting: SortInfo<DocumentsQuerySorting>) => {
  const sort = uniqBy(
    [
      {
        sort: sorting.sort,
        direction: sorting.direction,
      },
      {
        sort: 'creationDate',
        direction: sorting.direction,
      },
      {
        sort: 'createdAt',
        direction: sorting.direction,
      },
      {
        sort: 'id',
        direction: sorting.direction,
      },
    ],
    (x) => x.sort,
  );
  return jsonAPISortBuilder(sort);
};

const documentsFiltersToJsonApiFilters = (filters: DocumentsQueryFilters): IJsonAPIFilterBuilder => {
  const builder = jsonAPIFilterBuilder();
  if (!isNil(filters.totalAmount)) {
    if (!isNil(filters.totalAmount[0])) builder.gte('grossAmountAmount', filters.totalAmount[0]);
    if (!isNil(filters.totalAmount[1])) builder.lte('grossAmountAmount', filters.totalAmount[1]);
    if (!isNil(filters.totalAmount[2])) {
      const isInFlow = filters.totalAmount[2] === TransactionFlowDirection.Inflow ? true : false;
      builder.eq('isInflow', isInFlow);
    }
  }
  if (!isNil(filters.creationDate?.[0])) {
    const creationDate = formatISOForAPI(filters.creationDate[0]);
    builder.gte('creationDate', creationDate);
  }
  if (!isNil(filters.creationDate?.[1])) {
    const creationDate = formatDateForAPI(endOfDay(parseISO(filters.creationDate[1])));
    builder.lte('creationDate', creationDate);
  }

  if (!isNil(filters.deliveryDate?.[0])) {
    builder.gte('searchDate', filters.deliveryDate[0]);
  }
  if (!isNil(filters.deliveryDate?.[1])) {
    const deliveryDate = endOfDay(parseISO(filters.deliveryDate[1]));
    builder.lt('searchDate', deliveryDate.toISOString());
  }

  if (!isNil(filters.acceptanceDate?.[0])) {
    builder.gte('searchAcceptanceDate', filters.acceptanceDate[0]);
  }
  if (!isNil(filters.acceptanceDate?.[1])) {
    const acceptanceDate = endOfDay(parseISO(filters.acceptanceDate[1]));
    builder.lt('searchAcceptanceDate', acceptanceDate.toISOString());
  }

  if (!isNil(filters.detectionDatetime?.[0])) {
    builder.gte('detectionDatetime', filters.detectionDatetime[0]);
  }
  if (!isNil(filters.detectionDatetime?.[1])) {
    builder.lt('detectionDatetime', filters.detectionDatetime[1]);
  }

  if (!isNil(filters.direction)) builder.eq('documentDirection', filters.direction);
  if (!isNil(filters.rawDirection)) builder.eq('direction', filters.rawDirection);
  if (filters.creators?.length) builder.in('creator', filters.creators);
  if (!isNil(filters.paymentStatus)) builder.eq('paymentStatus', filters.paymentStatus);
  if (filters.documentTypes?.length) builder.in('documentType', filters.documentTypes);
  if (filters.currencies?.length) builder.in('grossAmountCurrency', filters.currencies);

  if (filters.search) builder.ilike('search', filters.search);
  if (filters.searchBills) builder.ilike('searchBills', filters.searchBills);

  if (filters.category) addCategoryFilter(builder, filters.category);

  if (filters.costCenter === null) {
    builder.empty('cost_centers_id', true);
  } else if (filters.costCenter && filters.costCenter.length >= 1) {
    builder.in('cost_centers_id', filters.costCenter);
  }

  if (filters.withNumber) builder.empty('number', false);
  if (filters.number) {
    builder.eq('number', filters.number);
  }
  if (filters.numberIcase) builder.eq('numberIcase', filters.numberIcase);

  if (filters.status?.length) builder.in('status', filters.status);
  if (filters.counterpart?.length) builder.in('counterpart.id', filters.counterpart);
  if (filters.counterpartCountry) builder.eq('counterpart.country', filters.counterpartCountry);

  if (filters.ids) {
    builder.in('id', filters.ids);
  }
  if (filters.excludeIds && filters.excludeIds.length > 0) {
    builder.notIn('id', filters.excludeIds);
  }

  if (isDefined(filters.flowCount)) {
    const { operator, value } = filters.flowCount;
    switch (operator) {
      case '=':
        builder.eq('flowCount', value);
        break;
      case '<':
        builder.lt('flowCount', value);
        break;
      case '<=':
        builder.lte('flowCount', value);
        break;
      case '>=':
        builder.gte('flowCount', value);
        break;
      case '>':
        builder.gt('flowCount', value);
        break;
    }
  }

  if (isDefined(filters.flowHasPaymentMethod) && filters.flowHasPaymentMethod.length > 0) {
    builder.in('flowHasPaymentMethod', filters.flowHasPaymentMethod);
  }
  if (isDefined(filters.flowHasPaymentStatus)) {
    builder.eq('flowHasPaymentStatus', filters.flowHasPaymentStatus);
  }
  if (isDefined(filters.flowHasExpectedPaymentDate?.[0])) {
    const creationDate = formatISOForAPI(filters.flowHasExpectedPaymentDate[0]);
    builder.gte('flowHasExpectedPaymentDate', creationDate);
  }
  if (isDefined(filters.flowHasExpectedPaymentDate?.[1])) {
    const creationDate = formatDateForAPI(addDays(parseISO(filters.flowHasExpectedPaymentDate[1]), 1));
    builder.lt('flowHasExpectedPaymentDate', creationDate);
  }
  if (isDefined(filters.hasVATRegistrations)) {
    builder.eq('hasVatRegistrations', filters.hasVATRegistrations ? 'true' : 'false');
  }
  if (isDefined(filters.hasBookkeepingTransactions)) {
    builder.eq('hasBookkeepingTransactions', filters.hasBookkeepingTransactions ? 'true' : 'false');
  }
  if (isDefined(filters.hasWithholdingFlow)) {
    builder.eq('has_withholding_flow', filters.hasWithholdingFlow ? 'true' : 'false');
  }
  if (isDefined(filters.nonWithholdingFlowPaymentDate?.[0])) {
    const date = formatISOForAPI(filters.nonWithholdingFlowPaymentDate[0]);
    builder.gte('non_withholding_flow_payment_date', date);
  }
  if (isDefined(filters.nonWithholdingFlowPaymentDate?.[1])) {
    const date = formatDateForAPI(addDays(parseISO(filters.nonWithholdingFlowPaymentDate[1]), 1));
    builder.lt('non_withholding_flow_payment_date', date);
  }
  if (isDefined(filters.withholdingFlowPaymentDate?.[0])) {
    const date = formatISOForAPI(filters.withholdingFlowPaymentDate[0]);
    builder.gte('withholding_flow_payment_date', date);
  }
  if (isDefined(filters.withholdingFlowPaymentDate?.[1])) {
    const date = formatDateForAPI(addDays(parseISO(filters.withholdingFlowPaymentDate[1]), 1));
    builder.lt('withholding_flow_payment_date', date);
  }
  if (isDefined(filters.withholdingFlowPaymentStatus)) {
    builder.eq('withholding_flow_payment_status', filters.withholdingFlowPaymentStatus);
  }
  if (isDefined(filters.isElectronic)) {
    builder.eq('is_e_invoice', filters.isElectronic ? 'true' : 'false');
  }

  if (isDefined(filters.deviceIds) && filters.deviceIds.length > 0) {
    builder.in('deviceId', filters.deviceIds);
  }
  if (isDefined(filters.billType)) {
    builder.empty('deviceId', filters.billType === BillTypeFilter.Other);
  }

  return builder;
};

/**
 * @deprecated Please use the `fetchDocuments` function from `src/api/documents.ts` instead.
 */
export const fetchDocuments = async (
  companyId: Company['id'],
  filters: DocumentsQueryFilters = emptyDocumentsQueryFilters,
  options: {
    size: number;
    cursor?: string;
    sorting: SortInfo<DocumentsQuerySorting>;
    include?: string[];
  } = { size: DocumentsPageSize, sorting: initialDocumentsQuerySorting },
) => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'flows',
    'flows.bookkeepingEntry',
    'allocations',
    'allocations.category',
    'allocations.subcategory',
    'allocations.costCenter',
    'category',
    'subcategory',
    'counterpart',
    ...(options.include ?? []),
  ]);

  const filterBuilder = documentsFiltersToJsonApiFilters(filters);
  filterBuilder.eq('company.id', companyId);

  if (filters.includeHidden === false) filterBuilder.empty('hiddenAt', true);

  const sortingBuilder = createSortingBuilder(options.sorting);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options.size);
  if (options.cursor) paginationParamsBuilder.cursor(options.cursor);

  const searchParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, sortingBuilder, paginationParamsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: DocumentsMeta }>('/api/v1/documents', {
    params: searchParams,
  });

  return {
    documents: deserialize<Document>(data) as Document[],
    page: (data.meta as DocumentsMeta).page,
  };
};

const fetchDocumentNumbers = async (
  companyId: Company['id'],
  documentType: DocumentType | undefined,
  documentDirection: DocumentDirection,
  creationDate: string,
  creators: DocumentCreator[] | undefined,
  options: {
    size: number;
    sorting: SortInfo<DocumentsQuerySorting>;
  } = { size: DocumentsPageSize, sorting: initialDocumentsQuerySorting },
) => {
  const parsedDate = parseISO(creationDate);
  const from = formatDateForAPI(startOfYear(parsedDate));
  const to = formatDateForAPI(endOfYear(parsedDate));
  const filterBuilder = documentsFiltersToJsonApiFilters({
    currencies: [],
    creationDate: [from, to],
    rawDirection: documentDirection,
    documentTypes: documentType ? [documentType] : [],
    paymentStatus: null,
    search: '',
    totalAmount: null,
    withNumber: true,
    creators,
  });
  filterBuilder.eq('company.id', companyId);
  filterBuilder.neq('status', DocumentStatus.Draft);

  const sortingBuilder = createSortingBuilder(options.sorting);
  const fieldsBuilder = jsonAPIFieldsBuilder([
    {
      resource: 'document',
      fields: ['number', 'source', 'progressive'],
    },
  ]);

  const includeBuilder = jsonAPIIncludeBuilder(['sectional']);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options.size);

  const searchParams = buildJsonAPIQueryParams(
    filterBuilder,
    fieldsBuilder,
    sortingBuilder,
    paginationParamsBuilder,
    includeBuilder,
  );
  const { data } = await backendApiGateway.get<DocumentObject & { meta: DocumentsMeta }>('/api/v1/documents', {
    params: searchParams,
  });

  const documentList = deserialize<Document & { number: string }>(data) as (Document & { number: string })[];
  return documentList.map((d) => ({
    number: d.number,
    progressive: d.progressive ? BigInt(d.progressive) : null,
    sectionalId: d.sectional?.id ?? null,
    source: d.source,
  }));
};

const fetchLastDocumentDate = async (companyId: Company['id'], documentType: DocumentType, creationDate?: Date) => {
  const filterBuilder = documentsFiltersToJsonApiFilters({
    currencies: [],
    creationDate: [null, null],
    direction: documentType === DocumentType.SelfInvoice ? null : DocumentDirection.issued,
    documentTypes: [documentType],
    paymentStatus: null,
    search: '',
    totalAmount: null,
    withNumber: true,
    status: [
      DocumentStatus.Delivered,
      DocumentStatus.Created,
      DocumentStatus.Not_Delivered,
      DocumentStatus.Refused,
      DocumentStatus.Sent,
    ],
  });
  if (creationDate) filterBuilder.gt('creationDate', formatDateForAPI(creationDate));
  filterBuilder.eq('company.id', companyId);
  filterBuilder.empty('number', false);

  const sortingBuilder = createSortingBuilder({ sort: 'creationDate', direction: 'desc' });

  const fieldsBuilder = jsonAPIFieldsBuilder([
    {
      resource: 'document',
      fields: ['number', 'creationDate'],
    },
  ]);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(1);

  const searchParams = buildJsonAPIQueryParams(filterBuilder, fieldsBuilder, sortingBuilder, paginationParamsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: DocumentsMeta }>('/api/v1/documents', {
    params: searchParams,
  });

  const documentList = deserialize<Document & { number: string }>(data) as (Document & { number: string })[];
  return documentList.map((d) => ({ number: d.number, creationDate: d.creationDate }));
};

const fetchDocumentNumberExists = async (
  companyId: Company['id'],
  documentType: DocumentType | undefined,
  excludeId: Document['id'] | undefined,
  number: string,
  creationDate: string,
  direction?: DocumentDirection,
  creators?: DocumentCreator[],
) => {
  const parsedDate = parseISO(creationDate);
  const from = formatDateForAPI(startOfYear(parsedDate));
  const to = formatDateForAPI(endOfYear(parsedDate));

  const filterBuilder = documentsFiltersToJsonApiFilters({
    currencies: [],
    creationDate: [from, to],
    rawDirection: direction,
    documentTypes: documentType ? [documentType] : [],
    paymentStatus: null,
    search: '',
    totalAmount: null,
    numberIcase: number,
    creators,
  });
  filterBuilder.eq('company.id', companyId);
  if (excludeId) {
    filterBuilder.neq('id', excludeId);
  }
  filterBuilder.neq('status', DocumentStatus.Draft);

  const sortingBuilder = createSortingBuilder({ sort: 'creationDate', direction: 'desc' });

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(1);

  const searchParams = buildJsonAPIQueryParams(filterBuilder, sortingBuilder, paginationParamsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: DocumentsMeta }>('/api/v1/documents', {
    params: searchParams,
  });

  return deserialize<Document>(data) as Document[];
};

interface ExportDocumentsParameters {
  filters: DocumentsQueryFilters;
  companyId: Company['id'];
  selected: Document['id'][];
  excluded: Document['id'][];
  sorting: SortInfo<DocumentsQuerySorting>;
}

const exportDocuments = async ({ filters, companyId, selected, excluded, sorting }: ExportDocumentsParameters) => {
  const builder = documentsFiltersToJsonApiFilters(filters);
  if (selected.length > 0) {
    builder.in('id', selected);
  }
  if (excluded.length > 0) {
    builder.notIn('id', excluded);
  }
  builder.eq('company.id', companyId);

  const sortingBuilder = createSortingBuilder(sorting);

  const params = buildJsonAPIQueryParams(builder, sortingBuilder);

  const { data } = await backendApiGateway.get<Blob>('/api/v1/documents', {
    headers: {
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    responseType: 'blob',
    params,
  });

  downloadBlob(data, 'documenti.xlsx');
};

export const fetchDocument = async (
  id: Document['id'],
  options?: {
    include?: string[];
  },
): Promise<DocumentWithAttachments> => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'attachments',
    'allocations',
    'allocations.category',
    'allocations.subcategory',
    'allocations.costCenter',
    'bookkeepingEntries',
    'category',
    'company',
    'company.companyIdentity',
    'counterpart',
    'counterpart.parent',
    'flows',
    'flows.account',
    'flows.bookkeepingEntry',
    'flows.reconciliations',
    'flows.payments',
    'subcategory',
    'related', // LUCA to remove
    'referencedBy',
    ...(options?.include ?? []),
  ]);
  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/documents/${id}`, { params: searchParams });
  const document = deserialize<DocumentWithAttachments>(data) as DocumentWithAttachments;

  const flowsWithReconciliatons = await decorateWithReconciliations(document.flows, ['transaction']);
  return {
    ...document,
    flows: flowsWithReconciliatons,
  };
};

const createDocument = async (
  document: CreateDocumentForm['document'],
  companyId: string,
  withholdingTax?: CreateDocumentForm['withholdingTax'],
) => {
  const vatAmount = document.vatAmount;
  const grossAmount = document.totalAmount;
  const isInflow = getIsInflow(document.documentType, document.direction);
  const moneyVatAmount = { amount: vatAmount, currency: document.currency };
  const invoiceData: InvoiceCreationData = {
    ...document,
    creationDate: formatDateForAPI(document.creationDate),
    isInflow,
    vatAmount: moneyVatAmount,
    grossAmount: { amount: grossAmount, currency: document.currency },
    company: { id: companyId },
    counterpart: isDefined(document.counterpart) ? { id: document.counterpart } : null,
    category: isDefined(document.category) ? { id: document.category } : null,
    subcategory: isDefined(document.subcategory) ? { id: document.subcategory } : null,
    vatAmountCompensation:
      document.documentType === DocumentType.SelfInvoice
        ? { amount: '0', currency: document.currency }
        : moneyVatAmount,
    withholdingTaxDetails:
      withholdingTax && withholdingTax.length > 0
        ? withholdingTax.map((w) => ({
            type: w.type,
            motivation: w.motivation,
            percentage: w.percentage,
            taxableAmount: { amount: w.taxableAmount, currency: document.currency },
          }))
        : null,
  };
  const payload = serialize(invoiceData, DocumentResourceType, {
    relationships: ['company', 'category', 'subcategory', 'counterpart'],
  });

  const filterBuilder = jsonAPIIncludeBuilder(['company', 'category', 'subcategory', 'counterpart']);
  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/documents', payload, { params: searchParams });

  return deserialize<Document>(data) as Document;
};

export const invalidateDocumentsAndFlowsCache = () => {
  queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: OutstandingKeys.all });
  queryClient.invalidateQueries({ queryKey: ReconciliationsQueryKeys.all });
};

export const useCreateDocument = () => {
  const companyId = useActiveCompanyId();
  const createFlow = useCreateFlow();
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: async ({ document, flows, flowsShared, withholdingTax }: CreateDocumentForm) => {
      const createdInvoice = await createDocument(document, companyId, withholdingTax);

      const createdFlows = await Promise.all(
        flows.map((flow) =>
          createFlow.mutateAsync({
            amount: { amount: flow.totalAmount, currency: document.currency },
            paymentDate: flow.expectedPaymentDate,
            expectedPaymentDate: flow.expectedPaymentDate,
            paymentStatus: flow.paymentStatus,
            document: { id: createdInvoice.id },
            account: flowsShared.account ? { id: flowsShared.account } : null,
            paymentMethod: flowsShared.paymentMethod,
            paymentAccount: flowsShared.paymentAccount,
            payeeName: flowsShared.payeeName || null,
            notes: flow.notes,
          }),
        ),
      );

      return {
        document: createdInvoice,
        documentFlows: createdFlows,
      };
    },
    onError: (error, values) => {
      captureException(error, {
        extra: {
          ...values.document,
          ...values.flowsShared,
        },
      });
    },
    onSettled: () => {
      invalidateDocumentsAndFlowsCache();
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.company(companyId) });
    },
  });
};

class DocumentAllocationTransformer extends Transformer<DocumentAllocationUpdatePayload, {}> {
  constructor() {
    super();
    this.type = DocumentAllocationResourceType;
    this.relationships = {
      category: this.buildRelationship('category', CategoryResourceType),
      subcategory: this.buildRelationship('subcategory', SubcategoryResourceType),
      costCenter: this.buildRelationship('costCenter', CostCenterResourceType),
    };
  }

  transform(payload: DocumentAllocationUpdatePayload): JsonObject {
    return whitelist(payload, ['amount', 'itemDescription', 'lineNumber']);
  }

  buildRelationship(field: keyof DocumentAllocationUpdatePayload, resourceType: string) {
    return function (payload: Partial<DocumentAllocationUpdatePayload>) {
      return transform().withInput(payload[field]).withTransformer(new DefaultTransformer(resourceType)).toContext();
    };
  }
}

class DocumentUpdateTransformer extends Transformer<DocumentUpdatePayload, {}> {
  private readonly relationshipKeys = ['category', 'subcategory', 'counterpart', 'allocations'];

  constructor() {
    super();
    this.type = DocumentResourceType;
    this.relationships = {
      category: this.buildRelationship('category', CategoryResourceType),
      subcategory: this.buildRelationship('subcategory', SubcategoryResourceType),
      counterpart: this.buildRelationship('counterpart', CounterpartResourceType),
      allocations: this.buildAllocationsRelationship(),
    };
  }

  transform(payload: DocumentUpdatePayload): JsonObject {
    const keys = Object.keys(payload).filter((key) => !this.relationshipKeys.includes(key));
    return whitelist(payload, keys);
  }

  buildRelationship(field: keyof DocumentUpdatePayload, resourceType: string) {
    return function (payload: Partial<DocumentUpdatePayload>) {
      return transform().withInput(payload[field]).withTransformer(new DefaultTransformer(resourceType)).toContext();
    };
  }

  buildAllocationsRelationship() {
    return function (payload: Partial<DocumentUpdatePayload>) {
      return transform<DocumentAllocationUpdatePayload, {}>()
        .withInput(payload.allocations as DocumentAllocationUpdatePayload[])
        .withTransformer(new DocumentAllocationTransformer())
        .withIncluded(true)
        .toContext();
    };
  }
}

const updateDocument = async ({ id, newValues }: { id: Document['id']; newValues: UpdateDocumentForm }) => {
  const payload = transform<DocumentUpdatePayload, {}>()
    .withInput({
      id,
      documentType: newValues.documentType,
      creationDate: newValues.creationDate ? formatDateForAPI(newValues.creationDate) : undefined,
      number: newValues.number,
      counterpartName: newValues.counterpartName,
      grossAmount: isDefined(newValues.totalAmount) ? newMoney(newValues.totalAmount, newValues.currency) : undefined,
      vatAmount: isDefined(newValues.vatAmount) ? newMoney(newValues.vatAmount, newValues.currency) : undefined,
      counterpart: isDefined(newValues.counterpart) ? { id: newValues.counterpart } : newValues.counterpart,
      category: isDefined(newValues.category) ? { id: newValues.category } : newValues.category,
      subcategory: isDefined(newValues.subcategory) ? { id: newValues.subcategory } : newValues.subcategory,
      notes: newValues.notes,
      allocations: newValues.allocations,
    })
    .withTransformer(new DocumentUpdateTransformer())
    .serialize();

  const includeBuilder = jsonAPIIncludeBuilder([
    'category',
    'subcategory',
    'counterpart',
    'allocations',
    'allocations.category',
    'allocations.subcategory',
    'allocations.costCenter',
  ]);
  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/documents/${id}`, payload, {
    params: searchParams,
  });

  return deserialize<Document>(data) as Document;
};

const updateDocumentCategory = async ({
  id,
  category,
  subcategory,
}: {
  id: Document['id'];
  category: { id: Category['id'] } | null;
  subcategory: { id: Subcategory['id'] } | null;
}) => {
  const payload = serialize(
    {
      id,
      category,
      subcategory,
    },
    DocumentResourceType,
    {
      relationships: ['category', 'subcategory'],
    },
  );

  const includeBuilder = jsonAPIIncludeBuilder(['category', 'subcategory', 'counterpart']);
  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/documents/${id}`, payload, {
    params: searchParams,
  });

  return deserialize<Document>(data) as Document;
};

const updateDocumentNotes = async ({ id, notes }: { id: Document['id']; notes: Document['notes'] }) => {
  const payload = serialize(
    {
      id,
      notes,
    },
    DocumentResourceType,
  );

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/documents/${id}`, payload);

  return deserialize<Document>(data) as Document;
};
const bulkUpdateDocuments = async ({
  values,
  filters,
  companyId,
  selected,
  excluded,
}: {
  values: DocumentBulkUpdate;
  filters: DocumentsQueryFilters;
  companyId: Company['id'];
  selected: Document['id'][];
  excluded: Document['id'][];
}) => {
  const builder = documentsFiltersToJsonApiFilters(filters);
  if (selected.length > 0) {
    builder.in('id', selected);
  }
  if (excluded.length > 0) {
    builder.notIn('id', excluded);
  }
  builder.eq('company.id', companyId);

  const params = buildJsonAPIQueryParams(builder);

  const result = await backendApiGateway.post<{ num: number }>(
    '/api/v1/documents/bulk-update',
    {
      attributes: values,
      companyId,
    },
    {
      headers: {
        'content-type': 'application/json',
      },
      params,
    },
  );
  return result;
};

const bulkDeleteDocuments = async ({
  companyId,
  selected,
  filters,
}: {
  companyId: Company['id'];
  selected: Document['id'][];
  filters: DocumentsQueryFilters;
}) => {
  if (isEmpty(selected) && isEmpty(filters)) {
    throw new Error('Cannot bulk delete without filters and IDs');
  }

  const builder = documentsFiltersToJsonApiFilters(filters);
  if (selected.length > 0) {
    builder.in('id', selected);
  }
  builder.eq('company.id', companyId);

  const params = buildJsonAPIQueryParams(builder);

  const result = await backendApiGateway.post<DocumentObject>(
    `/api/v1/documents/bulk-delete`,
    {
      companyId,
    },
    {
      headers: {
        'content-type': 'application/json',
      },
      params,
    },
  );
  return result;
};

interface UseDeleteDocumentVariables {
  documentId: Document['id'];
  flowIds: Flow['id'][];
}

const deleteDocument = async (vars: UseDeleteDocumentVariables) => {
  const { documentId: id } = vars;
  const { data } = await backendApiGateway.delete<DocumentObject>(`/api/v1/documents/${id}`);
  return deserialize<Document>(data) as Document;
};

interface UseUpdateDodcumentVariables {
  id: Document['id'];
  newValues: UpdateDocumentForm;
  updatedAttributes: string[];
}

const updateEventByDocumentType = (documentType: DocumentType) => {
  switch (documentType) {
    case DocumentType.Invoice:
      return AnalyticsEvents.DOCUMENT_INVOICE_EDITED;
    case DocumentType.Bill:
      return AnalyticsEvents.DOCUMENT_BILL_EDITED;
    case DocumentType.DeliveryNote:
      return AnalyticsEvents.DELIVERY_NOTES_EDITED;
    case DocumentType.Quote:
      return AnalyticsEvents.QUOTE_EDITED;
  }
};

export const useUpdateDocument = (analyticsContext: string) => {
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: (vars: UseUpdateDodcumentVariables) => updateDocument({ id: vars.id, newValues: vars.newValues }),
    onSuccess: (data, variables) => {
      const event = updateEventByDocumentType(data.documentType);

      if (event) {
        track(event, {
          context: analyticsContext,
          updatedAttribute: variables.updatedAttributes.join(', '),
        });
      }
      invalidateDocumentsAndFlowsCache();
    },
  });
};

export const useUpdateDocumentNotes = (analyticsContext: string) => {
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: updateDocumentNotes,
    onSuccess: (data) => {
      const event = updateEventByDocumentType(data.documentType);
      if (event) {
        track(event, {
          context: analyticsContext,
          updatedAttribute: ['notes'],
        });
      }

      invalidateDocumentsAndFlowsCache();
    },
  });
};

export const useUpdateDocumentCategory = () => {
  return useMutation({
    mutationFn: updateDocumentCategory,
    onSuccess: () => {
      invalidateDocumentsAndFlowsCache();
    },
  });
};

export const useDeleteDocument = () => {
  const { track } = useAnalytics();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: (_data, variables) => {
      invalidateDocumentsAndFlowsCache();
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.company(companyId) });
      queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.countByStatusAll });
      track(AnalyticsEvents.DOCUMENT_DELETED, { flowIds: variables.flowIds });
    },
  });
};

export const useBulkDeleteDocuments = () => {
  const { track } = useAnalytics();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: bulkDeleteDocuments,
    onSuccess: (_data, variables) => {
      invalidateDocumentsAndFlowsCache();
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.company(companyId) });
      queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.countByStatusAll });
      track(AnalyticsEvents.DOCUMENT_DELETED, { flowIds: variables.selected });
    },
  });
};

// A document is functionally a self-invoice when it is either stored as SELF_INVOICE or
// as INVOICE + RECEIVED (TD01 self-invoice). Used to route numeration lookups into the
// self-invoice pool.
const isSelfInvoice = (documentType: DocumentType, direction: DocumentDirection | undefined): boolean =>
  (documentType === DocumentType.Invoice && direction === DocumentDirection.received) ||
  documentType === DocumentType.SelfInvoice;

export const useFetchLastProgressiveBySectional = () => {
  const companyId = useActiveCompanyId();
  return useCallback(
    (documentType: DocumentType, sectionalId: string | null, creationDate: string, direction: DocumentDirection) => {
      return queryClient.fetchQuery({
        queryKey: DocumentQueryKeys.lastProgressiveBySectional(
          companyId,
          documentType,
          direction,
          sectionalId,
          creationDate,
        ),
        queryFn: async () => {
          if (!isSelfInvoice(documentType, direction)) {
            return fetchLastProgressiveBySectional(
              companyId,
              documentType,
              DocumentDirection.issued,
              undefined,
              sectionalId,
              creationDate,
            );
          }
          const [selfInvoiceProgressive, inHouseInvoiceProgressive] = await Promise.all([
            fetchLastProgressiveBySectional(
              companyId,
              DocumentType.SelfInvoice,
              DocumentDirection.received,
              undefined,
              sectionalId,
              creationDate,
            ),
            // Restrict to in-house creators: THIRD_PARTY/IMPORT TD01s carry external
            // numbering and would pollute the self-invoice progressive.
            fetchLastProgressiveBySectional(
              companyId,
              DocumentType.Invoice,
              DocumentDirection.received,
              [DocumentCreator.Builder, DocumentCreator.IntegrationApi],
              sectionalId,
              creationDate,
            ),
          ]);
          if (selfInvoiceProgressive === null) return inHouseInvoiceProgressive;
          if (inHouseInvoiceProgressive === null) return selfInvoiceProgressive;
          return selfInvoiceProgressive > inHouseInvoiceProgressive
            ? selfInvoiceProgressive
            : inHouseInvoiceProgressive;
        },
      });
    },
    [companyId],
  );
};

export const useLastDocumentsNumberQuery = (
  documentType?: DocumentType,
  direction?: DocumentDirection,
  creationDate?: string,
) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: DocumentQueryKeys.lastDocumentsNumber(companyId, documentType, direction, creationDate),
    queryFn: async () => {
      const options = { size: 100, sorting: { sort: 'creationDate' as const, direction: 'desc' as const } };
      if (!documentType || !isSelfInvoice(documentType, direction)) {
        return fetchDocumentNumbers(
          companyId,
          documentType,
          DocumentDirection.issued,
          creationDate ?? '',
          undefined,
          options,
        );
      }
      const [selfInvoices, inHouseInvoices] = await Promise.all([
        fetchDocumentNumbers(
          companyId,
          DocumentType.SelfInvoice,
          DocumentDirection.received,
          creationDate ?? '',
          undefined,
          options,
        ),
        // Restrict to in-house creators: THIRD_PARTY/IMPORT TD01s carry external
        // numbering and would pollute the self-invoice suggestion.
        fetchDocumentNumbers(
          companyId,
          DocumentType.Invoice,
          DocumentDirection.received,
          creationDate ?? '',
          [DocumentCreator.Builder, DocumentCreator.IntegrationApi],
          options,
        ),
      ]);
      return [...selfInvoices, ...inHouseInvoices];
    },
    enabled: !!companyId && !!documentType && !!direction && !!creationDate,
  });
};

export const useLastDocumentCreationDate = ({
  documentType,
}: {
  documentType: DocumentType;
}): { creationDate: string } | null => {
  const companyId = useActiveCompanyId();

  const query = useQuery({
    queryKey: DocumentQueryKeys.latestDocumentCreationDate(companyId, documentType),
    queryFn: () => fetchLastDocumentDate(companyId, documentType),
    enabled: !!companyId,
  });

  return query.data?.[0] ?? null;
};

export const useInvoiceNumberExistence = (
  invoice: DocumentWithAttachments | undefined,
  number: string,
  enabled: boolean,
): {
  data: Document | null;
  isLoading: boolean;
} => {
  const companyId = useActiveCompanyId();
  const shouldFetchDocumentWithSameNumber = enabled && !!companyId && !!number && !!invoice?.creationDate;

  // Collision is checked within the same pool only — Solution #1 splits numerations
  // between fattura (INVOICE+ISSUED) and self-invoice (SELF_INVOICE+RECEIVED plus
  // in-house INVOICE+RECEIVED).
  const isSelfInv = !!invoice && isSelfInvoice(invoice.documentType, invoice.direction);

  const invoiceQuery = useQuery({
    queryKey: DocumentQueryKeys.documentNumberExists(
      companyId,
      number,
      invoice?.documentType,
      invoice?.id,
      invoice?.creationDate,
    ),
    queryFn: () =>
      fetchDocumentNumberExists(
        companyId,
        invoice?.documentType,
        invoice?.id,
        number,
        invoice?.creationDate || '',
        invoice?.direction,
      ),
    enabled: shouldFetchDocumentWithSameNumber && !isSelfInv,
  });

  const selfInvoiceQuery = useQuery({
    queryKey: DocumentQueryKeys.documentNumberExists(
      companyId,
      number,
      DocumentType.SelfInvoice,
      invoice?.id,
      invoice?.creationDate,
    ),
    queryFn: () =>
      fetchDocumentNumberExists(
        companyId,
        DocumentType.SelfInvoice,
        invoice?.id,
        number,
        invoice?.creationDate || '',
        DocumentDirection.received,
      ),
    enabled: shouldFetchDocumentWithSameNumber && isSelfInv,
  });

  // Restrict to in-house creators: THIRD_PARTY/IMPORT TD01s carry external
  // numbering and would produce false-positive collisions.
  const inHouseInvoiceQuery = useQuery({
    queryKey: [
      ...DocumentQueryKeys.documentNumberExists(
        companyId,
        number,
        DocumentType.Invoice,
        invoice?.id,
        invoice?.creationDate,
      ),
      'in-house',
    ],
    queryFn: () =>
      fetchDocumentNumberExists(
        companyId,
        DocumentType.Invoice,
        invoice?.id,
        number,
        invoice?.creationDate || '',
        DocumentDirection.received,
        [DocumentCreator.Builder, DocumentCreator.IntegrationApi],
      ),
    enabled: shouldFetchDocumentWithSameNumber && isSelfInv,
  });

  return {
    data: invoiceQuery.data?.[0] ?? selfInvoiceQuery.data?.[0] ?? inHouseInvoiceQuery.data?.[0] ?? null,
    isLoading: invoiceQuery.isLoading || selfInvoiceQuery.isLoading || inHouseInvoiceQuery.isLoading,
  };
};

export const useCheckStrictCreationDate = (
  documentType: DocumentType,
  creationDate: Date,
): { number: string; creationDate: string } | null => {
  const companyId = useActiveCompanyId();

  const query = useQuery({
    queryKey: DocumentQueryKeys.latestDocumentCreationDate(companyId, documentType, creationDate),
    queryFn: () => fetchLastDocumentDate(companyId, documentType, creationDate),
    enabled: !!companyId,
  });

  return query.data?.[0] ?? null;
};

const emptyInvoicesDocumentQuery: DocumentsQueryFilters = {
  ...emptyDocumentsQueryFilters,
  documentTypes: [
    DocumentType.Invoice,
    DocumentType.CreditNote,
    DocumentType.DebitNote,
    DocumentType.Parcel,
    DocumentType.SelfInvoice,
  ],
};

export const useInvoicesTableQueryFilters = (direction: DocumentDirection): DocumentsQueryFilters => {
  const filters = useFilterState<InvoicesFiltersFields>();
  const hasSplitByCostCenter = useHasSplitByCostCenter();

  const adjustedDirection =
    direction === DocumentDirection.issued &&
    filters.status.length === 1 &&
    filters.status.includes(DocumentStatus.Draft)
      ? null
      : direction;

  return {
    totalAmount: filters.amountRange,
    creationDate: filters.dateRange,
    documentTypes: filters.documentType.length
      ? filters.documentType
      : [
          DocumentType.Invoice,
          DocumentType.CreditNote,
          DocumentType.DebitNote,
          DocumentType.Parcel,
          DocumentType.SelfInvoice,
          DocumentType.Receipt,
        ],
    direction: adjustedDirection,
    currencies: filters.currency ? [filters.currency] : [],
    paymentStatus: filters.paymentStatus,
    category: filters.category ?? undefined,
    costCenter: hasSplitByCostCenter ? filters.costCenter : undefined,
    search: filters.search,
    status: filters.status,
  };
};

interface UseDocumentInfiniteQueryOptions {
  enabled?: boolean;
  include?: string[];
}

export const useDocumentInfiniteQuery = (filters: DocumentsQueryFilters, options?: UseDocumentInfiniteQueryOptions) => {
  const companyId = useActiveCompanyId();
  const enabled = isDefined(companyId) && (options?.enabled ?? true);

  const sorting = initialDocumentsQuerySorting;
  const include = ['flows.reconciliations', ...(options?.include ?? [])];

  return useInfiniteQuery({
    queryKey: [...DocumentQueryKeys.companyInfinityList(companyId, filters, sorting), { include }],
    queryFn: ({ pageParam }) =>
      fetchDocuments(companyId, filters, {
        size: DocumentsPageSize,
        cursor: pageParam,
        sorting,
        include,
      }).then(async (data) => {
        return {
          ...data,
          documents: await filterOutReconciliationsWithDeletedTransactions(data.documents),
        };
      }),
    initialPageParam: '',
    placeholderData: keepPreviousData,
    getNextPageParam: ({ page: { cursor }, documents }: { page: MetaDataCursorPage; documents: Document[] }) => {
      if (documents.length >= DocumentsPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled,
  });
};

const filterOutReconciliationsWithDeletedTransactions = async (documents: Document[]): Promise<Document[]> => {
  const decoratedFlows = await decorateWithReconciliations(
    documents.flatMap((d) => d.flows).filter((f) => !!f),
    ['transaction', 'transaction.bookkeepingTransaction'],
  );
  const flowsMap = new Map(decoratedFlows.map((flow) => [flow.id, flow]));
  return documents.map((d) => {
    const decoratedFlows = d.flows?.map((f) => flowsMap.get(f.id)).filter((f) => !!f) ?? [];
    d.flows = decoratedFlows;
    return d;
  });
};

export const useInvoicesTableInfiniteQuery = (direction: DocumentDirection) => {
  const companyId = useActiveCompanyId();

  const filters = useInvoicesTableQueryFilters(direction);
  const sorting = useTableSorting<DocumentsQuerySorting>();

  return useInfiniteQuery({
    queryKey: DocumentQueryKeys.companyInfinityList(companyId, filters, sorting),
    queryFn: ({ pageParam }) =>
      fetchDocuments(companyId, filters, {
        size: DocumentsPageSize,
        cursor: pageParam,
        sorting,
      }),
    initialPageParam: '',
    placeholderData: keepPreviousData,
    getNextPageParam: ({ page: { cursor }, documents }: { page: MetaDataCursorPage; documents: Document[] }) => {
      if (documents.length >= DocumentsPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !!companyId,
    refetchOnMount: false,
    refetchInterval: 300_000,
  });
};

const emptyBillsDocumentQuery: DocumentsQueryFilters = {
  ...omit(emptyDocumentsQueryFilters, 'search'),
  searchBills: '',
  documentTypes: [DocumentType.Bill],
};

export const useBillsTableQueryFilters = (): DocumentsQueryFilters => {
  const filters = useFilterState<BillsFiltersFields>();
  return {
    totalAmount: filters.amountRange,
    creationDate: filters.dateRange,
    direction: null,
    documentTypes: [DocumentType.Bill],
    currencies: filters.currency ? [filters.currency] : [],
    paymentStatus: filters.paymentStatus,
    searchBills: filters.search,
    category: filters.category ?? undefined,
    status: [],
  };
};

export const useBillsTableInfiniteQuery = () => {
  const companyId = useActiveCompanyId();

  const filters = useBillsTableQueryFilters();
  const sorting = useTableSorting<DocumentsQuerySorting>();

  return useInfiniteQuery({
    queryKey: DocumentQueryKeys.companyInfinityList(companyId, filters, sorting),
    queryFn: ({ pageParam }) =>
      fetchDocuments(companyId, filters, {
        size: DocumentsPageSize,
        cursor: pageParam,
        sorting,
      }),
    initialPageParam: '',
    placeholderData: keepPreviousData,
    getNextPageParam: ({ page: { cursor }, documents }: { page: MetaDataCursorPage; documents: Document[] }) => {
      if (documents.length >= DocumentsPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !!companyId,
  });
};

const useDeliveryNotesTableQueryFilters = (): DocumentsQueryFilters => {
  const filters = useFilterState<DeliveryNotesFiltersFields>();

  return {
    totalAmount: null,
    creationDate: filters.dateRange,
    direction: null,
    documentTypes: [DocumentType.DeliveryNote],
    currencies: filters.currency ? [filters.currency] : [],
    paymentStatus: null,
    search: filters.search,
    category: undefined,
    status: filters.status,
  };
};

export const useDeliveryNotesMultipleAsidePreview = (limit: number) => {
  const companyId = useActiveCompanyId();
  const basicFilters = useDeliveryNotesTableQueryFilters();
  const selected = useSelected();
  const excluded = useExcluded();

  const filters = {
    ...basicFilters,
    excludeIds: excluded.length ? excluded : undefined,
    ids: selected.length ? selected : undefined,
  };
  const sorting = useTableSorting<DocumentsQuerySorting>();

  return useQuery({
    queryKey: DocumentQueryKeys.deliveryNotesMultipleAsidePreview(companyId, filters, sorting),
    queryFn: () =>
      fetchDocuments(companyId, filters, {
        size: limit,
        sorting,
        include: [],
      }),
    enabled: !!companyId,
  });
};

export const useDeliveryNotesTableInfiniteQuery = () => {
  const companyId = useActiveCompanyId();

  const filters = useDeliveryNotesTableQueryFilters();
  const sorting = useTableSorting<DocumentsQuerySorting>();

  return useInfiniteQuery({
    queryKey: DocumentQueryKeys.companyInfinityList(companyId, filters, sorting),
    queryFn: ({ pageParam }) =>
      fetchDocuments(companyId, filters, {
        size: DocumentsPageSize,
        cursor: pageParam,
        sorting,
        include: ['related', 'referencedBy'],
      }),
    initialPageParam: '',
    placeholderData: keepPreviousData,
    getNextPageParam: ({ page: { cursor }, documents }: { page: MetaDataCursorPage; documents: Document[] }) => {
      if (documents.length >= DocumentsPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !!companyId,
  });
};

export const useDocumentQuery = (id: Document['id'] | undefined) => {
  return useQuery({
    queryKey: DocumentQueryKeys.single(id || ''),
    queryFn: () => fetchDocument(id!),
    enabled: !!id,
  });
};

export const usePayrollDownloadUrlQuery = (id: Document['id'] | undefined) => {
  return useQuery({
    queryKey: DocumentQueryKeys.downloadUrl(id ?? ''),
    queryFn: () => fetchPayrollDownloadUrl(id!),
    enabled: !!id,
  });
};

export const useBulkUpdateBillsTable = () => {
  const filters = useBillsTableQueryFilters();
  const companyId = useActiveCompanyId();
  const selected = useSelected();
  const excluded = useExcluded();
  const resetSelection = useResetSelection();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: (values: DocumentBulkUpdate) =>
      bulkUpdateDocuments({
        values,
        filters,
        companyId,
        selected,
        excluded,
      }),
    onSuccess: (_, data) => {
      resetSelection();
      invalidateDocumentsAndFlowsCache();
      const updatedAttributes = Object.keys(omitBy(data, isUndefined));
      track(AnalyticsEvents.INVOICES_BULK_UPDATED, { updatedAttributes, context: '/invoices' });
    },
  });
};

export const useBulkUpdateInvoicesTable = (direction: DocumentDirection) => {
  const filters = useInvoicesTableQueryFilters(direction);
  const companyId = useActiveCompanyId();
  const selected = useSelected();
  const excluded = useExcluded();
  const resetSelection = useResetSelection();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: (values: DocumentBulkUpdate) =>
      bulkUpdateDocuments({
        values,
        filters,
        companyId,
        selected,
        excluded,
      }),
    onSuccess: (_, data) => {
      resetSelection();
      invalidateDocumentsAndFlowsCache();
      const updatedAttributes = Object.keys(omitBy(data, isUndefined));
      track(AnalyticsEvents.INVOICES_BULK_UPDATED, { updatedAttributes, context: '/invoices' });
    },
  });
};

export const useBulkUpdateDocuments = (options: { context: string; eventName: AnalyticsEvents }) => {
  const companyId = useActiveCompanyId();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: ({ values, filters }: { values: DocumentBulkUpdate; filters: DocumentsQueryFilters }) => {
      assertIsDefined(companyId, 'Company id must be defined in order to bulk update documents.');
      return bulkUpdateDocuments({
        values,
        filters,
        companyId,
        selected: [],
        excluded: [],
      });
    },
    onSuccess: (_, data) => {
      invalidateDocumentsAndFlowsCache();
      const updatedAttributes = Object.keys(omitBy(data, isUndefined));
      track(options.eventName, { updatedAttributes, context: options.context, filters: data.filters });
    },
  });
};

export const useExportSelectedBills = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const companyId = useActiveCompanyId();
  const filters = useBillsTableQueryFilters();
  const sorting = useTableSorting<DocumentsQuerySorting>();
  const selected = useSelected();
  const excluded = useExcluded();

  return useMutation({
    mutationFn: async () =>
      await exportDocuments({
        filters,
        companyId,
        selected,
        excluded,
        sorting,
      }),

    onError: () => snack.error(t('ERROR_EXPORTING')),
    onSettled: () => track(AnalyticsEvents.TRANSACTIONS_EXPORTED),
  });
};

export const useExportSelectedInvoices = (direction: DocumentDirection) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const companyId = useActiveCompanyId();
  const filters = useInvoicesTableQueryFilters(direction);
  const sorting = useTableSorting<DocumentsQuerySorting>();
  const selected = useSelected();
  const excluded = useExcluded();

  return useMutation({
    mutationFn: async () =>
      await exportDocuments({
        filters,
        companyId,
        selected,
        excluded,
        sorting,
      }),
    onError: () => snack.error(t('ERROR_EXPORTING')),
    onSettled: () => track(AnalyticsEvents.TRANSACTIONS_EXPORTED),
  });
};

export const useExportInvoices = (direction: DocumentDirection) => {
  const companyId = useActiveCompanyId();
  const sorting = useTableSorting<DocumentsQuerySorting>();
  const filters = useInvoicesTableQueryFilters(direction);
  return useMutation({
    mutationFn: () => exportDocuments({ companyId, excluded: [], filters, selected: [], sorting }),
  });
};

export const useExportBills = () => {
  const companyId = useActiveCompanyId();
  const sorting = useTableSorting<DocumentsQuerySorting>();
  const filters = useBillsTableQueryFilters();
  return useMutation({
    mutationFn: () => exportDocuments({ companyId, excluded: [], filters, selected: [], sorting }),
  });
};

const fetchAttachment = async (id: Document['id']) => {
  const { data } = await backendApiGateway.get<{ url: string }>(`/api/v1/documents/${id}/attachment`);
  return data.url;
};

export const useDocumentAttachment = (id: Document['id']) => {
  return useMutation({
    mutationFn: async () => await fetchAttachment(id),
  });
};

const fetchMetadata = async (companyId: Company['id'], filters: DocumentsQueryFilters) => {
  const filterBuilder = documentsFiltersToJsonApiFilters(filters);
  filterBuilder.eq('company.id', companyId);

  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<{
    total: number;
    totalsMoney: Total[];
    totalsEur: Total;
    hasFlowBookkeepingRegistration: boolean;
  }>('/api/v1/documents/metadata', {
    params: searchParams,
  });

  return data;
};

export const useInvoicesMetadata = (filters: DocumentsQueryFilters, enabled?: boolean) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: DocumentQueryKeys.metadata(companyId, filters),
    queryFn: () => fetchMetadata(companyId, filters),
    enabled: enabled !== false && !!companyId,
    staleTime: Infinity,
  });
};

export const useInvoicesTableMetadata = (direction: DocumentDirection, enabled?: boolean) => {
  const filters = useInvoicesTableQueryFilters(direction);
  return useInvoicesMetadata(filters, enabled);
};

export const useBillsMetadata = (filters: DocumentsQueryFilters, enabled?: boolean) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: DocumentQueryKeys.metadata(companyId, filters),
    queryFn: () => fetchMetadata(companyId, filters),
    enabled: enabled !== false && !!companyId,
    staleTime: Infinity,
  });
};

export const useBillsTableMetadata = () => {
  const filters = useBillsTableQueryFilters();
  return useBillsMetadata(filters);
};

const fetchDocumentsSummary = async ({ companyId, date }: InvoiceDashboardParams) => {
  const filters = jsonAPIFilterBuilder();
  filters.eq('company.id', companyId);
  filters.gte('creationDate', formatISO(date[0], { representation: 'date' }));
  filters.lte('creationDate', formatISO(date[1], { representation: 'date' }));
  filters.in('documentType', ['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE', 'BILL', 'SELF_INVOICE', 'PARCEL']);
  filters.notIn('status', [DocumentStatus.Draft, DocumentStatus.Discarded]);
  filters.empty('hiddenAt', true);
  const extraQueryBuilder = jsonAPIExtraQueryBuilder();
  const queryParams = buildJsonAPIQueryParams(filters, extraQueryBuilder);
  const { data } = await backendApiGateway.get<DocumentsSummaryResponse>('/api/v1/documents-dashboard/summary', {
    params: queryParams,
  });
  return data;
};

interface UseDocumentsSummaryOptions {
  sortCounterparts?: boolean;
}

export const useDocumentsSummary = ({ sortCounterparts = false }: UseDocumentsSummaryOptions = {}) => {
  const companyId = useActiveCompanyId();
  const date = useDateRange();
  return useQuery({
    enabled: Boolean(companyId),
    queryKey: DocumentQueryKeys.documentSummary(date),
    queryFn: () => fetchDocumentsSummary({ companyId, date }),
    select: (data) =>
      sortCounterparts
        ? {
            ...data,
            customers: sortCounterpartSummary(data.customers),
            suppliers: sortCounterpartSummary(data.suppliers),
          }
        : data,
  });
};

export const useMarkInovicesUncategorized = () => {
  const companyId = useActiveCompanyId();
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: ({ categoryId, subcategoryId }: { categoryId: Category['id']; subcategoryId: Subcategory['id'] }) =>
      bulkUpdateDocuments({
        values: { categoryId: null, subcategoryId: null },
        companyId,
        filters: {
          ...emptyInvoicesDocumentQuery,
          category: {
            type: 'subcategories',
            categoryId,
            subcategoryId: [subcategoryId],
          },
        },
        selected: [],
        excluded: [],
      }),
    onSuccess() {
      invalidateDocumentsAndFlowsCache();
      track(AnalyticsEvents.INVOICES_CATEGORIES_BULK_UPDATED);
    },
  });
};

export const useMarkBillsUncategorized = () => {
  const companyId = useActiveCompanyId();
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: ({ categoryId, subcategoryId }: { categoryId: Category['id']; subcategoryId: Subcategory['id'] }) =>
      bulkUpdateDocuments({
        values: { categoryId: null, subcategoryId: null },
        companyId,
        filters: {
          ...emptyBillsDocumentQuery,
          category: {
            type: 'subcategories',
            categoryId,
            subcategoryId: [subcategoryId],
          },
        },
        selected: [],
        excluded: [],
      }),
    onSuccess() {
      invalidateDocumentsAndFlowsCache();
      track(AnalyticsEvents.BILLS_CATEGORIES_BULK_UPDATED);
    },
  });
};

export const useDeliveryNotesTableMetadata = (enabled?: boolean) => {
  const companyId = useActiveCompanyId();
  const filters = useDeliveryNotesTableQueryFilters();

  return useQuery({
    queryKey: DocumentQueryKeys.metadata(companyId, filters),
    queryFn: () => fetchMetadata(companyId, filters),
    enabled: enabled !== false && !!companyId,
    staleTime: Infinity,
  });
};

// QUOTES

const useQuotesTableQueryFilters = (): DocumentsQueryFilters => {
  const filters = useFilterState<QuotesFiltersFields>();

  return {
    totalAmount: null,
    creationDate: filters.dateRange,
    direction: null,
    documentTypes: [DocumentType.Quote],
    currencies: filters.currency ? [filters.currency] : [],
    paymentStatus: null,
    search: filters.search,
    category: undefined,
    status: filters.status,
  };
};

export const useQuotesTableMetadata = (enabled?: boolean) => {
  const filters = useQuotesTableQueryFilters();
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: DocumentQueryKeys.metadata(companyId, filters),
    queryFn: () => fetchMetadata(companyId, filters),
    enabled: enabled !== false && !!companyId,
    staleTime: Infinity,
  });
};

export const useQuotesTableInfiniteQuery = () => {
  const companyId = useActiveCompanyId();

  const filters = useQuotesTableQueryFilters();
  const sorting = useTableSorting<DocumentsQuerySorting>();

  return useInfiniteQuery({
    queryKey: DocumentQueryKeys.companyInfinityList(companyId, filters, sorting),
    queryFn: ({ pageParam }) =>
      fetchDocuments(companyId, filters, {
        size: DocumentsPageSize,
        cursor: pageParam,
        sorting,
        include: ['related', 'referencedBy'],
      }),
    initialPageParam: '',
    placeholderData: keepPreviousData,
    getNextPageParam: ({ page: { cursor }, documents }: { page: MetaDataCursorPage; documents: Document[] }) => {
      if (documents.length >= DocumentsPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: !!companyId,
  });
};

const updateVisibility = async ({ id, hiddenAt }: { id: Document['id']; hiddenAt: Date | null }) => {
  const payload = serialize(
    {
      id,
      hiddenAt,
    },
    DocumentResourceType,
  );

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/documents/${id}`, payload);

  return deserialize<Document>(data) as Document;
};

export const useToggleDocumentVisibility = () => {
  const { track } = useAnalytics();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (document: Document) => {
      const hiddenAt = document.hiddenAt ? null : new Date();
      return updateVisibility({ id: document.id, hiddenAt });
    },
    onSuccess: (data) => {
      track(data.hiddenAt ? AnalyticsEvents.DOCUMENT_HIDDEN : AnalyticsEvents.DOCUMENT_UNHIDDEN);
      invalidateDocumentsAndFlowsCache();
    },
    onError: () => snack.error(t('ENCOUNTERED_ERROR')),
  });
};

// CREDIT NOTES
export const useAssociatedCreditNotes = (
  creditNote: DocumentWithAttachments,
  relatedInvoice?: DocumentWithAttachments,
) => {
  const updateFlow = useUpdateFlow('credit-notes-association');
  const creditNoteflowUpdateSideEffects = useFlowUpdateSideEffects({
    dataTestId: 'credit-notes-association',
    document: creditNote,
  });
  const relatedDocumentflowUpdateSideEffects = useFlowUpdateSideEffects({
    dataTestId: 'credit-notes-association',
    document: creditNote.related[0],
  });
  const createFlow = useCreateFlow();

  if (relatedInvoice === undefined) {
    return undefined;
  }
  if (creditNote.documentType !== DocumentType.CreditNote || relatedInvoice.documentType !== DocumentType.Invoice) {
    return undefined;
  }

  const creditNoteflow = creditNote.flows[0];
  const relatedInvoiceFlow = relatedInvoice.flows[0];

  if (creditNote.flows.length !== 1 || !creditNoteflow || relatedInvoice.flows.length !== 1 || !relatedInvoiceFlow) {
    return undefined;
  }

  const amountDifference = subtractMoney(relatedInvoice.grossAmount, creditNote.grossAmount);

  if (isZero(amountDifference)) {
    // The credit note and invoice flows are both set as PAID
    return async () => {
      const updatedCreditNoteFlow = await updateFlow.mutateAsync({
        id: creditNoteflow.id,
        updatedAttributes: ['paymentStatus'],
        payloadData: {
          paymentStatus: FlowPaymentStatus.Paid,
        },
        flowDirection: creditNote.isInflow ? 'inflow' : 'outflow',
      });

      creditNoteflowUpdateSideEffects(creditNoteflow, updatedCreditNoteFlow);

      const updatedRelatedFlow = await updateFlow.mutateAsync({
        id: relatedInvoiceFlow.id,
        updatedAttributes: ['paymentStatus'],
        payloadData: {
          paymentStatus: FlowPaymentStatus.Paid,
        },
        flowDirection: relatedInvoice.isInflow ? 'inflow' : 'outflow',
      });
      relatedDocumentflowUpdateSideEffects(relatedInvoiceFlow, updatedRelatedFlow);
    };
  }

  if (isMoreThanZero(amountDifference)) {
    // The credit note and invoice flows are both set as PAID
    return async () => {
      const updatedCreditNoteFlow = await updateFlow.mutateAsync({
        id: creditNoteflow.id,
        updatedAttributes: ['paymentStatus'],
        payloadData: {
          paymentStatus: FlowPaymentStatus.Paid,
        },
        flowDirection: creditNote.isInflow ? 'inflow' : 'outflow',
      });

      creditNoteflowUpdateSideEffects(creditNoteflow, updatedCreditNoteFlow);

      const updatedRelatedFlow = await updateFlow.mutateAsync({
        id: relatedInvoiceFlow.id,
        updatedAttributes: ['paymentStatus'],
        payloadData: {
          paymentStatus: FlowPaymentStatus.Paid,
          amount: creditNoteflow.amount,
        },
        flowDirection: relatedInvoice.isInflow ? 'inflow' : 'outflow',
      });
      relatedDocumentflowUpdateSideEffects(relatedInvoiceFlow, updatedRelatedFlow);

      await createFlow.mutateAsync({
        expectedPaymentDate: new Date(relatedInvoiceFlow.expectedPaymentDate),
        paymentStatus: FlowPaymentStatus.ToPay,
        paymentMethod: relatedInvoiceFlow.paymentMethod,
        paymentAccount: relatedInvoiceFlow.paymentAccount,
        amount: amountDifference,
        notes: null,
        document: { id: relatedInvoice.id },
        paymentDate: new Date(relatedInvoiceFlow.expectedPaymentDate),
        account: relatedInvoiceFlow.account?.id ? { id: relatedInvoiceFlow.account.id } : null,
      });
    };
  }
};

export const useMetadata = (filters: DocumentsQueryFilters, enabled?: boolean) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: DocumentQueryKeys.metadata(companyId, filters),
    queryFn: () => fetchMetadata(companyId, filters),
    enabled: enabled !== false && !!companyId,
    staleTime: Infinity,
  });
};

interface CreateDocumentAssociationParams {
  documentId: Document['id'];
  relatedDocumentId: Document['id'];
}

const createDocumentAssociation = async (documentAssociation: CreateDocumentAssociationParams) =>
  await backendApiGateway.post<DocumentObject>(
    `/api/v1/documents/${documentAssociation.documentId}/associate-document`,
    {
      relatedDocumentId: documentAssociation.relatedDocumentId,
    },
  );

export const useCreateDocumentAssociation = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (documentAssociation: CreateDocumentAssociationParams) =>
      createDocumentAssociation(documentAssociation),
    onSuccess: () => {
      invalidateDocumentsAndFlowsCache();
    },
    onError: () => snack.error(t('ENCOUNTERED_ERROR')),
  });
};

export const useDownloadDocumentXml = (documentId: string | undefined) => {
  const attachment = useDocumentAttachment(documentId || '');
  return async () => {
    const url = await attachment.mutateAsync();
    downloadUrl(url);
  };
};

const fetchBillsWithoutVATRegistrations = async (
  companyId: Company['id'],
  filters: BillsWithoutVATRegistrationsFilter,
) => {
  const { creationDate, deviceId, billType, detectionDatetime } = filters;

  const documents = await fetchDocuments(
    companyId,
    {
      documentTypes: [DocumentType.Bill],
      creationDate: [
        creationDate[0] ? formatISO(creationDate[0], { representation: 'date' }) : null,
        creationDate[1] ? formatISO(creationDate[1], { representation: 'date' }) : null,
      ],
      ...(detectionDatetime
        ? {
            detectionDatetime: [
              detectionDatetime[0] ? formatISO(detectionDatetime[0]) : null,
              detectionDatetime[1] ? formatISO(detectionDatetime[1]) : null,
            ],
          }
        : {}),
      hasVATRegistrations: false,
      deviceIds: deviceId ? [deviceId] : [],
      billType,
    },
    {
      size: 100,
      sorting: {
        sort: 'searchDate',
        direction: 'asc',
      },
    },
  );

  return documents.documents;
};

export const useBillsWithoutVATRegistrations = (filters: BillsWithoutVATRegistrationsFilter, enabled: boolean) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: DocumentQueryKeys.billsWithoutVATRegistration(companyId, filters),
    queryFn: () => fetchBillsWithoutVATRegistrations(companyId, filters),
    enabled: !!companyId && enabled,
  });
};

export const useBillsWithoutVATRegistrationsSuggestions = (filters: BillsWithoutVATRegistrationsFilter) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: DocumentQueryKeys.billsWithoutVATRegistrationSuggestions(companyId, filters),
    queryFn: () => fetchBillsWithoutVATRegistrations(companyId, filters),
    enabled: !!companyId,
  });
};

export const useDocumentImportTemplates = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: DocumentImportQueryKeys.importTemplates(companyId),
    queryFn: () => fetchDocumentImportTemplates({ companyId }),
    enabled: Boolean(companyId),
    retry: false,
  });
};

export const useDocumentImportConfig = () => {
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: ({
      templateId,
      documentType,
    }: {
      templateId: InvoiceImportTemplate['id'];
      documentType?: DocumentType;
    }) => fetchDocumentImportConfig({ companyId, templateId, documentType }),
  });
};
